import MsDyn365 from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { IPhbMobileMenuContext, PhbMobileMenuContext } from '../utils/phb-mobile-menu-context';

/** Keep in sync with header.scss and navigation-menu.view.tsx */
const DESKTOP_MIN_WIDTH = 1024;

interface IHeaderState {
    isMegaOpen: boolean;
    isMobileMenuOpen: boolean;
    /** Set by the navigation menu when it is one level deep; shows the Back arrow in the drawer's top bar */
    mobileBack?: () => void;
}

export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, IHeaderState> {
    private readonly megaContainerRef = React.createRef<HTMLDivElement>();
    private readonly productsLinkRef = React.createRef<HTMLAnchorElement>();
    private readonly drawerPanelRef = React.createRef<HTMLDivElement>();
    private menuContext?: IPhbMobileMenuContext;
    private menuContextKey = '';

    state: IHeaderState = { isMegaOpen: false, isMobileMenuOpen: false, mobileBack: undefined };

    componentDidMount(): void {
        const config = this.props.config as any;
        if (config?.useStickyHeader && MsDyn365.isBrowser) {
            window.addEventListener('scroll', this._configureStickyHeader);
            this._configureStickyHeader();
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('mousedown', this._handleOutsideMegaClick);
            document.addEventListener('keydown', this._handleKeyDown);
            window.addEventListener('resize', this._handleResize);
        }
    }

    componentDidUpdate(_prevProps: unknown, prevState: IHeaderState): void {
        if (MsDyn365.isBrowser && prevState.isMobileMenuOpen !== this.state.isMobileMenuOpen) {
            // Stop the page scrolling behind the open drawer
            document.body.style.overflow = this.state.isMobileMenuOpen ? 'hidden' : '';
            if (this.state.isMobileMenuOpen) {
                this.drawerPanelRef.current?.focus();
            }
        }
    }

    componentWillUnmount(): void {
        const config = this.props.config as any;
        if (config?.useStickyHeader && MsDyn365.isBrowser) {
            window.removeEventListener('scroll', this._configureStickyHeader);
        }
        if (MsDyn365.isBrowser) {
            document.removeEventListener('mousedown', this._handleOutsideMegaClick);
            document.removeEventListener('keydown', this._handleKeyDown);
            window.removeEventListener('resize', this._handleResize);
            document.body.style.overflow = '';
        }
    }

    /** Close desktop mega when clicking outside the panel (visibility is controlled here, not in the nav module). */
    private _handleOutsideMegaClick = (e: MouseEvent) => {
        if (!this.state.isMegaOpen) {
            return;
        }
        const target = e.target;
        if (!(target instanceof Element)) {
            return;
        }
        const mega = this.megaContainerRef.current;
        const products = this.productsLinkRef.current;
        if (mega?.contains(target)) {
            return;
        }
        if (products?.contains(target)) {
            return;
        }
        this.setState({ isMegaOpen: false });
    };

    private _handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') {
            return;
        }
        if (this.state.isMobileMenuOpen) {
            this.closeMobileMenu();
        } else if (this.state.isMegaOpen) {
            this.closeMega();
        }
    };

    /** Drawer is a small-screen thing: close it if the window grows into desktop size */
    private _handleResize = () => {
        if (this.state.isMobileMenuOpen && window.innerWidth >= DESKTOP_MIN_WIDTH) {
            this.closeMobileMenu();
        }
    };

    private closeMega = () => this.setState({ isMegaOpen: false });

    private openMobileMenu = () => this.setState({ isMobileMenuOpen: true, isMegaOpen: false });

    private closeMobileMenu = () => this.setState({ isMobileMenuOpen: false, mobileBack: undefined });

    private setMobileBack = (handler?: () => void) => this.setState({ mobileBack: handler });

    private handleProductsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const config = this.props.config as any;
        const productsLink = config?.productsLink;
        if (typeof window === 'undefined') return;
        const isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH;

        if (isDesktop) {
            e.preventDefault();
            this.setState(prev => ({ isMegaOpen: !prev.isMegaOpen }));
        } else {
            if (!productsLink?.destinationUrl) e.preventDefault();
        }
    };

    private getActiveLink(): 'home' | 'products' | 'contact' | '' {
        if (typeof window === 'undefined') {
            return '';
        }
        const url = window.location.href.toLowerCase();

        // Contact Us page
        if (url.includes('contactus')) {
            return 'contact';
        }

        // PLP (.c?) or PDP (.p?)
        if (url.includes('.c?') || url.includes('.p?')) {
            return 'products';
        }

        // Default (home)
        return 'home';
    }

    /** Link config can be a plain string or a link object with destinationUrl */
    private static toHref(value: unknown): string | undefined {
        if (typeof value === 'string') {
            return value || undefined;
        }
        const url = (value as { destinationUrl?: string } | undefined)?.destinationUrl;
        return url || undefined;
    }

    /** Server-safe page-type check — runs during SSR, no client wait, no flicker. */
    private getHeaderStyle(): 'transparent' | 'solid' {
        const requestUrl = (this.props.context as any)?.request?.url?.requestUrl;
        const path = (requestUrl?.pathname ?? '').toLowerCase();
        const full = (requestUrl?.href ?? path).toLowerCase();

        // Home page
        if (path === '/' || path === '') {
            return 'transparent';
        }

        // PLP / Category page
        if (full.includes('.c?') || full.includes('.c/') || path.includes('/category')) {
            return 'transparent';
        }

        // Search results page
        if (full.includes('/search') || full.includes('searchresultpage')) {
            return 'transparent';
        }

        return 'solid';
    }

    /** What the navigation menu needs for the drawer. Rebuilt only when the config or the open state changes. */
    private getMenuContext(): IPhbMobileMenuContext {
        const config = this.props.config as any;
        const homeHref = HeaderView.toHref(config?.homeLink);
        const contactHref = HeaderView.toHref(config?.contactLink);
        const productsHref = HeaderView.toHref(config?.productsLink);

        const key = JSON.stringify([
            homeHref,
            config?.homeLinkText,
            contactHref,
            config?.contactLinkText,
            productsHref,
            config?.productsLinkText,
            this.state.isMobileMenuOpen
        ]);

        if (!this.menuContext || key !== this.menuContextKey) {
            this.menuContextKey = key;
            this.menuContext = {
                homeLink: homeHref && config?.homeLinkText ? { text: config.homeLinkText, href: homeHref } : undefined,
                contactLink: contactHref && config?.contactLinkText ? { text: config.contactLinkText, href: contactHref } : undefined,
                productsText: config?.productsLinkText || 'Products',
                productsHref,
                isMenuOpen: this.state.isMobileMenuOpen,
                setBackHandler: this.setMobileBack
            };
        }

        return this.menuContext;
    }

    private renderPrimaryLinks() {
        const config = this.props.config as any;
        const { homeLinkText, homeLink, productsLinkText, productsLink, contactLink, contactLinkText } = config || {};

        const active = this.getActiveLink();
        return (
            <div className='phibro-header__primary-links'>
                {homeLink && (
                    <a
                        href={homeLink}
                        className={'phibro-header__primary-link' + (active === 'home' ? ' active' : '')}
                        onClick={this.closeMega}
                    >
                        {homeLinkText}
                    </a>
                )}

                <a
                    ref={this.productsLinkRef}
                    href={productsLink ?? '#'}
                    className={
                        'phibro-header__primary-link phibro-header__primary-link--products' +
                        (this.state.isMegaOpen ? ' open' : '') +
                        (active === 'products' ? ' active' : '')
                    }
                    onClick={this.handleProductsClick}
                >
                    {productsLinkText}
                </a>

                {contactLink && (
                    <a
                        href={contactLink}
                        className={'phibro-header__primary-link' + (active === 'contact' ? ' active' : '')}
                        onClick={this.closeMega}
                    >
                        {contactLinkText}
                    </a>
                )}
            </div>
        );
    }

    /**
     * Small screens: the one and only mobile menu. Slides in from the left when the hamburger is pressed.
     * The navigation menu module renders its drill-down inside it.
     */
    private renderMobileDrawer(): JSX.Element {
        const { isMobileMenuOpen, mobileBack } = this.state;

        return (
            <div className={'phb-drawer' + (isMobileMenuOpen ? ' phb-drawer--open' : '')}>
                <div className='phb-drawer__backdrop' onClick={this.closeMobileMenu} />

                <div
                    className='phb-drawer__panel'
                    id='phb-mobile-menu'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Menu'
                    tabIndex={-1}
                    ref={this.drawerPanelRef}
                >
                    <div className='phb-drawer__bar'>
                        {mobileBack && <button type='button' className='phb-drawer__back' aria-label='Back' onClick={mobileBack} />}
                        <button type='button' className='phb-drawer__close' aria-label='Close menu' onClick={this.closeMobileMenu} />
                    </div>

                    <div className='phb-drawer__body'>
                        <PhbMobileMenuContext.Provider value={this.getMenuContext()}>
                            {this._renderReactFragment(this.props.menuBar)}
                        </PhbMobileMenuContext.Provider>
                    </div>
                </div>
            </div>
        );
    }

    render(): JSX.Element | null {
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            HeaderBottomBarContainer,
            Divider,
            TierDivider,
            isRetailB2BContactMultiOutletOrderingFeatureEnabled
        } = this.props;

        const headerStyle = this.getHeaderStyle();

        return (
            <Module {...HeaderTag}>
                <Node
                    {...HeaderContainer}
                    className={`phibro-header phibro-header--${headerStyle} ${HeaderContainer.className}`}>
                    <Node {...HeaderTopBarContainer} className='phibro-header__bar'>
                        {/* Hamburger (below 1024px). The menu itself lives in the drawer at the end of the header. */}
                        <div className='mobile-navicon-container'>
                            <button
                                type='button'
                                className='phb-hamburger'
                                aria-label='Open menu'
                                aria-expanded={this.state.isMobileMenuOpen}
                                aria-controls='phb-mobile-menu'
                                onClick={this.openMobileMenu}
                            >
                                <span className='phb-hamburger__line' />
                                <span className='phb-hamburger__line' />
                                <span className='phb-hamburger__line' />
                            </button>
                        </div>

                        {this.props.logo}
                        {this._renderMobileLogo()}

                        <div className='phibro-header__search'>
                            {this.props.search?.map((x, i) => (
                                <React.Fragment key={i}>{x}</React.Fragment>
                            ))}
                        </div>

                        {this.renderPrimaryLinks()}

                        <div
                            className='phibro-header__cart'
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.location.href = '/cart'; // or your Cart URL
                            }}
                        >
                            {this.props.cartIcon}
                            <span className='phibro-header__cart-label'>Cart</span>
                        </div>

                        <div className='phibro-header__account'>
                            {this.props.accountInfoDropdownButton && (
                                <Node
                                    {...this.props.AccountInfoDropdownParentContainer}
                                    className={this.props.AccountInfoDropdownParentContainer?.className || ''}
                                >
                                    {this.props.accountInfoDropdownButton}

                                    {this.props.AccountInfoDropdownPopoverConentContainer && (
                                        <Node
                                            {...this.props.AccountInfoDropdownPopoverConentContainer}
                                            className={this.props.AccountInfoDropdownPopoverConentContainer?.className || ''}
                                        >
                                            {this.props.accountLinks?.map((l, i) => (
                                                <React.Fragment key={i}>{l}</React.Fragment>
                                            ))}
                                            {this.props.signOutLink}
                                        </Node>
                                    )}
                                </Node>
                            )}

                            {!this.props.accountInfoDropdownButton && this.props.signInLink}
                        </div>

                        <Node {...Divider} />
                        {!isRetailB2BContactMultiOutletOrderingFeatureEnabled && (
                            <>
                                {this.props.preferredStore}
                                {this.props.currentDistributor}
                            </>
                        )}
                        {this.props.currentDistributor}
                        {this.props.siteOptions}
                    </Node>

                    {isRetailB2BContactMultiOutletOrderingFeatureEnabled && (
                        <>
                            {TierDivider && Object.keys(TierDivider).length > 0 && <Node {...TierDivider} />}
                            {HeaderBottomBarContainer && Object.keys(HeaderBottomBarContainer).length > 0 && (
                                <Node {...HeaderBottomBarContainer}>
                                    {this.props.preferredStore}
                                    {this.props.currentDistributor}
                                </Node>
                            )}
                        </>
                    )}

                    {/* Desktop mega menu panel (1024px and up) */}
                    <div
                        ref={this.megaContainerRef}
                        className={'phibro-header__mega-container' + (this.state.isMegaOpen ? ' phibro-header__mega-container--open' : '')}
                    >
                        {this.props.menuBar}
                    </div>

                    {/* Small-screen drawer (below 1024px) */}
                    {this.renderMobileDrawer()}
                </Node>
            </Module>
        );
    }

    private _configureStickyHeader = (): void => {
        const headerAlertsContainer = document.querySelector('.ms-promo-banner') as HTMLElement | null;
        const bannerHeights = headerAlertsContainer?.offsetHeight ?? 0;

        const headerElement = document.querySelector('header .default-container') as HTMLElement | null;
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > 0) {
                headerElement.classList.add('lock-opaque', 'fixed');
            } else {
                headerElement.classList.remove('lock-opaque', 'fixed');
                headerElement.classList.remove('fixed');
            }
        }

        const stickyHeader = document.querySelector('.desktop-navicon-container') as HTMLElement | null;
        const headerLogo = document.querySelector('.ms-header__logo') as HTMLElement | null;

        if (stickyHeader && headerLogo) {
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const config = this.props.config as any;
        return (
            <Logo
                link={config?.logoLink}
                image={config?.mobileLogoImage ?? {}}
                className='ms-header__mobile-logo'
                gridSettings={this.props.context.request.gridSettings}
                requestContext={this.props.context.request}
                typeName={this.props.typeName}
            />
        );
    }

    private _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode, i: number) => <React.Fragment key={i}>{slot}</React.Fragment>)
                    : null}
            </>
        );
    };
}

export default HeaderView;
